import { Fragment, h } from 'preact'
import { AppRoute } from '../route'
import { AppStore } from '../../store'
import { goToParti } from '../../store/actions'
import { getStringOrThrow, RouteParam } from '../../../lib/simple-routing/params'
import { ConnectedHeader } from '../../components/Header/Header'
import { AppFrame } from '../../components/frames/AppFrame/AppFrame'
import { ConnectedPrimarHeading } from '../../components/headings/PrimarHeading/PrimarHeading'
import { ConnectedSekundarHeading } from '../../components/headings/SekundarHeading/SekundarHeading'
import { ResultatParatiContainer } from '../../components/resultater/ResultatParti/Resultat'
import {
  stederTilOppslutningTableItem,
  toOppslutningTableItem
} from '../../components/moduler/Oppslutning/mapping'
import { Oppslutning } from '../../components/moduler/Oppslutning/Oppslutning'
import { OppslutningKart } from '../../components/moduler/OppslutningKart/OppslutningKart'
import { isParti, isPartiValgResultat, isValgResultat, Parti } from '../../domain'
import { useAppState } from '../../../lib/redux-provider-nohooks'
import { withSectionErrorBoundary } from '../../components/shared/SectionErrorBoundary/SectionErrorBoundary'
import { selectParti } from '../../store/selectors/grunndata'
import { Nabopartier } from '../../components/navigasjon/Nabopartier/Nabopartier'
import { ConnectedStorstePartier } from '../../components/navigasjon/StorstePartier/StorstePartier'
import { ConnectedSperret } from '../../components/moduler/advarsler/Sperret/Sperret'
import { IkkeOpptalt } from '../../components/moduler/advarsler/IkkeOpptalt/IkkeOpptalt'
import { OppslutningHistorisk } from '../../components/moduler/OppslutningHistorisk/OppslutningHistorisk'
import { HasOppslutning } from '../../components/shared/HasOppslutning/HasOppslutning'
import { PastanderParti } from '../../components/moduler/PastanderParti/PastanderParti'
import { OppslutningFylkeKart } from '../../components/moduler/OppslutningFylkeKart/OppslutningFylkeKart'
import { Skille } from '../../components/shared/Decoration'

type PartisideMode = 'BEGGE' | 'KUN_KOMMUNEVALG' | 'KUN_FYLKESVALG'

function renderKommunevalgsresultat(resultat, parti, locale) {
  if (isPartiValgResultat(resultat)) {
    /*
     * Dersom et parti stiller i flere kommuner får vi et PartiValgResultat.
     * Her listes det opp hvordan partiet gjør det de ulike kommunene
     *
     * */
    return (
      <Fragment>
        <OppslutningHistorisk
          parti={resultat.parti}
          result={resultat.nasjonalt}
          valgtype="kommune"
        />
        <OppslutningKart
          parti={resultat.parti}
          items={stederTilOppslutningTableItem(resultat.steder, resultat.parti, locale)}
        />
        <Oppslutning
          grunndata={parti}
          items={stederTilOppslutningTableItem(resultat.steder, resultat.parti, locale)}
          maxItems={20}
          valgtype="kommune"
        />
        <ConnectedStorstePartier />
      </Fragment>
    )
  } else if (isValgResultat(resultat)) {
    const { opptalt, partier = [], geografi, pastander } = resultat
    /*
     * Dersom et parti stiller kun en kommune får vi et ValgResultat.
     * Her listes det opp hvordan partiet gjør det i kommunen det stiller i,
     * sammenlignet med de andre partiene i kommunen
     * */
    return (
      <Fragment>
        {(!opptalt || opptalt.forelopig === 0) && <IkkeOpptalt />}
        <Oppslutning
          grunndata={geografi}
          items={toOppslutningTableItem(resultat.partier, locale, true, parti)}
          maxItems={20}
          valgtype="kommune"
        />
        <HasOppslutning partier={partier}>
          <PastanderParti
            geografi={geografi}
            parti={parti}
            partiresultat={partier[0]}
            pastander={pastander}
          />
        </HasOppslutning>
        <Nabopartier
          partier={partier
            .filter(p => p.parti.id !== parti.id)
            .map(partiresultat => partiresultat.parti)}
        />
      </Fragment>
    )
  }
}

function renderFylkestingresultat(resultat, parti, locale, visNavigasjon) {
  if (isPartiValgResultat(resultat)) {
    /*
     * Dersom et parti stiller i flere fylker får vi et PartiValgResultat.
     * Her listes det opp hvordan partiet gjør det de ulike fylkene
     *
     * */
    return (
      <Fragment>
        <OppslutningFylkeKart
          parti={resultat.parti}
          items={stederTilOppslutningTableItem(resultat.steder, resultat.parti, locale)}
        />
        <Oppslutning
          grunndata={parti}
          items={stederTilOppslutningTableItem(resultat.steder, resultat.parti, locale)}
          maxItems={20}
          valgtype="fylke"
        />
        {visNavigasjon && <ConnectedStorstePartier />}
      </Fragment>
    )
  } else if (isValgResultat(resultat)) {
    const { opptalt, partier = [] } = resultat

    /*
     * Dersom et parti stiller kun i ett fylke får vi et ValgResultat.
     * Her listes det opp hvordan partiet gjør det i fylket det stiller i,
     * sammenlignet med de andre partiene i fylket
     * */
    return (
      <Fragment>
        {(!opptalt || opptalt.forelopig === 0) && <IkkeOpptalt />}
        <Oppslutning
          grunndata={parti}
          items={toOppslutningTableItem(resultat.partier, locale, true, parti)}
          maxItems={20}
          valgtype="fylke"
        />
        {visNavigasjon && (
          <Nabopartier
            partier={partier
              .filter(p => p.parti.id !== parti.id)
              .map(partiresultat => partiresultat.parti)}
          />
        )}
      </Fragment>
    )
  }
}

function getPartisideMode(currentPage: Parti): PartisideMode {
  if (currentPage.valg.length === 2) {
    return 'BEGGE'
  } else {
    if (currentPage.valg.length === 1 && currentPage.valg[0].valgKey === '2019/ko') {
      return 'KUN_KOMMUNEVALG'
    } else {
      return 'KUN_FYLKESVALG'
    }
  }
}

function _Partiinnhold() {
  const { page } = useAppState()
  if (!isParti(page.current)) {
    throw new Error('Current page er ikke parti')
  }

  switch (getPartisideMode(page.current)) {
    case 'KUN_KOMMUNEVALG':
      return (
        <Fragment>
          <ConnectedPrimarHeading type="kommune" />
          <ConnectedSperret type="kommune" />
          <ResultatParatiContainer type="kommune">
            {(resultat, parti, locale) => {
              return renderKommunevalgsresultat(resultat, parti, locale)
            }}
          </ResultatParatiContainer>
        </Fragment>
      )

    case 'KUN_FYLKESVALG':
      return (
        <Fragment>
          <ConnectedPrimarHeading type="fylke" />
          <ConnectedSperret type="fylke" />
          <ResultatParatiContainer type="fylke">
            {(resultat, parti, locale) => {
              return renderFylkestingresultat(resultat, parti, locale, true)
            }}
          </ResultatParatiContainer>
        </Fragment>
      )
    case 'BEGGE':
      return (
        <Fragment>
          <ConnectedPrimarHeading type="kommune" />
          <ConnectedSperret type="kommune" />
          <ResultatParatiContainer type="kommune">
            {(resultat, parti, locale) => {
              return renderKommunevalgsresultat(resultat, parti, locale)
            }}
          </ResultatParatiContainer>

          <Skille />

          <ConnectedSekundarHeading type="fylke" />
          <ConnectedSperret type="fylke" />
          <ResultatParatiContainer type="fylke">
            {(resultat, parti, locale) => {
              return renderFylkestingresultat(resultat, parti, locale, false)
            }}
          </ResultatParatiContainer>
        </Fragment>
      )
  }
}

const Partiinnhold = withSectionErrorBoundary(_Partiinnhold)

export const partiRoute: AppRoute = {
  path: `/valg/2019/resultat/:locale/parti/:id`,
  widget: false,
  title(store: AppStore) {
    const geo = store.getState().page.current
    if (isParti(geo)) {
      return { id: 'sidetittel-parti', values: { partinavn: geo.navn } }
    }
  },
  onRouteChanged: async (store: AppStore, params: RouteParam[]) => {
    const paramId = getStringOrThrow(params, 'id')
    const parti = selectParti(store.getState(), paramId)
    return store.dispatch(goToParti(parti))
  },
  render: () => {
    return (
      <AppFrame>
        <ConnectedHeader />
        <main>
          <Partiinnhold />
        </main>
      </AppFrame>
    )
  }
}
